import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { commissionsApi } from './api';
import type {
  AdjustInput,
  CalculateInput,
  CommissionAdjustment,
  CommissionRate,
  CommissionRateListParams,
  CommissionRateListResult,
  CommissionRateStats,
  CreateRateInput,
  SettleInput,
  SettlementListParams,
  SettlementListResult,
  SettlementStats,
  SettlementStatsParams,
  UpdateRateInput,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useCommissionRates(params: CommissionRateListParams): UseQueryResult<CommissionRateListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['commissions', tenantId, 'rates', params.page ?? 1, params.location_id ?? '', params.user_id ?? '', params.is_active ?? '', params.search ?? ''],
    queryFn: () => commissionsApi.rates(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCommissionRateStats(): UseQueryResult<CommissionRateStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['commissions', tenantId, 'rate-stats'],
    queryFn: () => commissionsApi.rateStats(),
    staleTime: 30_000,
  });
}

export function useCommissionRate(id: string | undefined): UseQueryResult<CommissionRate | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['commissions', tenantId, 'rate', id],
    queryFn: () => (id ? commissionsApi.rate(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateRate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRateInput) => commissionsApi.createRate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'rates'] });
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'rate-stats'] });
    },
  });
}

export function useUpdateRate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRateInput }) => commissionsApi.updateRate(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'rates'] });
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'rate-stats'] });
    },
  });
}

export function useDeactivateRate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commissionsApi.deactivateRate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'rates'] });
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'rate-stats'] });
    },
  });
}

export function useSettlements(params: SettlementListParams): UseQueryResult<SettlementListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['commissions', tenantId, 'settlements', params.page ?? 1, params.status ?? '', params.location_id ?? '', params.user_id ?? '', params.start_date ?? '', params.end_date ?? ''],
    queryFn: () => commissionsApi.settlements(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useSettlementStats(params: SettlementStatsParams): UseQueryResult<SettlementStats> {
  const tenantId = useTenantId();
  return useQuery({
    // Keyed under the 'settlements' prefix (but NOT by status/page) so the
    // calculate/settle mutations' invalidation refreshes the KPI cards too.
    queryKey: ['commissions', tenantId, 'settlements', 'stats', params.location_id ?? '', params.user_id ?? '', params.start_date ?? '', params.end_date ?? ''],
    queryFn: () => commissionsApi.settlementStats(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCalculateCommissions() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CalculateInput) => commissionsApi.calculate(input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'settlements'] }); },
  });
}

export function useSettleCommission() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SettleInput }) => commissionsApi.settle(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'settlements'] }); },
  });
}

export function useCancelSettlement() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => commissionsApi.cancel(id, reason),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'settlements'] }); },
  });
}

export function useAdjustSettlement() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AdjustInput }) => commissionsApi.adjust(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'settlements'] });
      qc.invalidateQueries({ queryKey: ['commissions', tenantId, 'adjustments', id] });
    },
  });
}

export function useSettlementAdjustments(id: string | undefined): UseQueryResult<CommissionAdjustment[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['commissions', tenantId, 'adjustments', id],
    queryFn: () => (id ? commissionsApi.adjustments(id) : Promise.resolve([])),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
